// @givmo/app-charity — the catalog-removal card: a DOOR into the shipped removal-request page
// (Org Machinery OM-17 design 3; D-CPO-35 (5); RULED D-CPO-38 (1), 2026-09-06; its words RULED
// D-CPO-69 question 11, 2026-10-23, Org Machinery OM-71).
//
// The composed settings page's Organization section carries this card beside *Deactivate
// organization* for a holder of `charity:remove_request` — the string off the standing read, never
// a tier (the host declares it in `console/console-nav.ts` `organizationSettings`, `grave: true`,
// so the composer orders it after every knob and before Deactivate). Its ONE control is a door into
// the shipped removal-request PAGE (`features/settings/`, CPA-WEB7: the confirm-first form, the
// pending panel, the fail-closed Owner gate, the D-CP-11 copy lock) — the card never reproduces
// that form (the mockups' R5 dialog was NOT chosen). The door's address is the page's path
// constant, never a literal: Charity Portal re-addresses the page in their own PR and the door
// follows.
//
// The words are RULED (D-CPO-69 question 11; Charity Portal reviews at the PR under its copy lock):
// Charity Portal's two lines from their removal page, verbatim. Under D-CP-94 (their CPA-BE57) a
// processed removal takes the charity off Givmo for donors and ends the team's access while owed
// grants still reach it, so D-CPO-38 (2)'s words ("…the listing reverts to unclaimed…") stopped being
// true, and "This does not deactivate the organization." went with them. The Deactivate dialog
// still says what IT is not (the ruled "…a separate request." line); a copy pin asserts its presence.
//
// While a request is OPEN the card's line states the shipped status ("Identity check pending" /
// "Processing removal" — `removal-decode.ts`) and the door reads "View request": the page's
// pending panel is the record. A Member holds no `charity:remove_request`, so the composer renders
// no Organization section for them — "a Member sees the status line and no door" is the removal
// PAGE's pending panel, which the GET (Owner-or-Member) still serves them.
//
// DATA CONTRACT: `org-settings-removal-card` · `-status` · `-door` (the composer's family; the page
// keeps its own `settings-removal-*`).

import { useQuery } from '@tanstack/react-query';
import { charityAdminListCharityRemovalRequestsOptions } from '@givmo/api-client/query';
import {
  Card,
  CardBody,
  saasButtonVariants,
  shouldInterceptClick,
  testIdProp,
} from '@givmo/design-system';
import type { OrganizationSettingsCardProps } from '@givmo/org-console';
import { EXTERNAL_STATUS_REFETCH_MS } from '../../lib/external-status-refresh';
import {
  extractRemovalRequests,
  openRemovalRequest,
  removalStatusLabel,
} from '../settings/removal-decode';
import { SETTINGS_ROUTES } from '../settings/settings-routes';

const PREFIX = 'org-settings-removal';

/** The RULED words (D-CPO-69 question 11), in one place so the pin and the render cannot drift. */
export const REMOVAL_CARD_BODY =
  'Removes this organization from Givmo for donors and ends your team’s access. Any grants your organization is owed still reach it, to your bank account if it’s approved for payments, otherwise by mailed check.';
export const REMOVAL_CARD_DOOR_LABEL = 'Request removal';
export const REMOVAL_CARD_OPEN_DOOR_LABEL = 'View request';
export function removalCardTitle(orgName: string): string {
  return `Remove ${orgName} from Givmo`;
}

export function OrgRemovalCard({ orgRef, config, onNavigate }: OrganizationSettingsCardProps) {
  const removalOptions = charityAdminListCharityRemovalRequestsOptions({
    path: { charity_id: orgRef },
  });
  const removalQuery = useQuery({
    ...removalOptions,
    refetchInterval: (query) =>
      openRemovalRequest(extractRemovalRequests(query.state.data))
        ? EXTERNAL_STATUS_REFETCH_MS
        : false,
  });
  const openRequest = openRemovalRequest(extractRemovalRequests(removalQuery.data));
  const orgName = (config.vocab.orgName ?? '').trim();
  // The RULED title carries the charity's NAME: until it has resolved the card withholds — the
  // Deactivate card's posture on the same fact — rather than render a noun in its place.
  if (orgName.length === 0) return null;
  const title = removalCardTitle(orgName);
  const href = SETTINGS_ROUTES.removal;
  return (
    <Card {...testIdProp(`${PREFIX}-card`)}>
      <CardBody className="flex flex-col gap-12 sm:flex-row sm:items-center sm:justify-between sm:gap-16">
        <div className="flex min-w-0 flex-col gap-4">
          <h3 className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">{title}</h3>
          <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {REMOVAL_CARD_BODY}
          </p>
          {openRequest !== null && (
            <p
              className="font-sans text-s13 font-w600 tracking-givmo text-text-strong"
              {...testIdProp(`${PREFIX}-status`)}
            >
              {removalStatusLabel(openRequest.status)}
            </p>
          )}
        </div>
        <a
          href={href}
          className={saasButtonVariants({
            size: 'md',
            tone: 'secondary',
            className: 'shrink-0 self-start no-underline sm:self-auto',
          })}
          onClick={(event) => {
            if (onNavigate === undefined || !shouldInterceptClick(event)) return;
            event.preventDefault();
            onNavigate(href);
          }}
          {...testIdProp(`${PREFIX}-door`)}
        >
          {openRequest !== null ? REMOVAL_CARD_OPEN_DOOR_LABEL : REMOVAL_CARD_DOOR_LABEL}
        </a>
      </CardBody>
    </Card>
  );
}
